import React, { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Box, Button, IconButton, Tooltip } from "@mui/material";

import { SiteByIdDTO, UserDTO } from "api";
import { EsgDeleteIcon, PlusIcon } from "assets";
import { CustomMaterialTable, DeleteDialog, useDialogControls } from "components";
import { useNotify } from "hooks";
import { useUserRole } from "recoils";

import { dataProvidersColumns } from "./consts";
import { useDeleteDataProvider } from "./hooks";
import { UserModal } from "./components";

export const DataProviders = ({ siteData, allowedUsers }: { siteData: SiteByIdDTO, allowedUsers: UserDTO[] }) => {
  const notify = useNotify();
  const userRole = useUserRole();
  const queryClient = useQueryClient();
  const deleteDataProviderControls = useDialogControls();

  const userRecords = JSON.parse(JSON.stringify(siteData?.users || []));

  const { mutate: deleteDataProviderRequest } = useDeleteDataProvider(notify, queryClient);

  const [addUserModalOpen, setAddUserModalOpen] = useState<boolean>(false);
  const [dataProviderId, setDataProviderId] = useState(0);

  const handleDeleteProvider = async (deleteUserId: number | undefined) => {
    if (siteData && deleteUserId) {
      const selectedDataProviderId = deleteUserId;

      deleteDataProviderRequest({
        siteId: siteData?.id,
        userId: selectedDataProviderId,
      }, {
        onSuccess: () => {
          deleteDataProviderControls.close();
        },
      });
    }
  };

  return (
    <Box sx={{
      px: 3,
      mt: 1,
    }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1 }}>
        <p style={{ fontSize: "16px", fontWeight: "500" }}>
          Data Providers
        </p>
        <Button
          sx={{ borderRadius: "8px", width: "200px", height: "36px" }}
          variant="contained"
          onClick={() => setAddUserModalOpen(true)}
          startIcon={<PlusIcon />}
          disabled={userRole.isAuditor || userRole.isDataProvider}
        >
          Add Data Provider
        </Button>
      </Box>
      <CustomMaterialTable
        columns={dataProvidersColumns}
        data={userRecords}
        enableRowActions
        enablePagination={false}
        enableTopToolbar={false}
        enableRowVirtualization={false}
        renderRowActions={({ row }) => (
          <Box
            sx={{
              height: "45px",
              alignItems: "center",
              display: "flex",
              paddingRight: "5px",
            }}
          >
            <Tooltip arrow placement="top" title="Delete">
              <IconButton
                sx={{ width: "35px", height: "35px" }}
                color="error"
                onClick={() => {
                  deleteDataProviderControls.open();
                  setDataProviderId(row.original.id as number)
                }
                }
              >
                <EsgDeleteIcon />
              </IconButton>
            </Tooltip>
          </Box>
        )}
        muiTableContainerProps={{
          sx: {
            width: "calc(100vw - 150px)",
            "& tbody tr td": {
              paddingY: "0px",
            },
            "& thead tr th": {
              paddingY: "12px",
            },
            "& thead tr th:first-of-type": {
              borderRight: "1px solid #e0e0e0",
              "& > div": { pl: "8px" },
            },
            "& tbody tr td:first-of-type": {
              borderRight: "1px solid #e0e0e0",
            },
          },
        }}
      />
      <UserModal
        isOpen={addUserModalOpen}
        onClose={() => setAddUserModalOpen(false)}
        users={allowedUsers}
        site={siteData || ({} as SiteByIdDTO)}
      />
      <DeleteDialog
        isOpen={deleteDataProviderControls.isOpen}
        onCancel={deleteDataProviderControls.close}
        onDeleteClick={() => handleDeleteProvider(dataProviderId)}
        target="Data Provider"
        title="Delete Data Provider?"
      />
    </Box>
  );
};